import React, { useState, useCallback, useEffect } from 'react';
import { supabase } from '../../../supabaseClient';
import { useAdmin } from '../../../context/AdminContext';
import ScheduleBuilder from '../../../pages/ScheduleBuilder';
import EditScheduledGameModal from '../modals/EditScheduledGameModal';
import { generateSchedulePoster, printScoreSheet } from '../../../utils/printUtils';

// Parse "YYYY-MM-DDTHH:mm:ss" without UTC shift
const parseLocalDate = (str) => {
    const [date, time = '00:00:00'] = (str || '').split('T');
    const [y, m, d]                 = date.split('-').map(Number);
    const [hh, mm]                  = time.split(':').map(Number);
    return new Date(y, m - 1, d, hh, mm);
};

const groupByDate = (games) => {
    const map = {};
    games.forEach(game => {
        const local = parseLocalDate(game.game_date);
        const key   = local.toLocaleDateString('en-US', {
            weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
        });
        if (!map[key]) map[key] = [];
        map[key].push(game);
    });
    return Object.keys(map)
        .sort((a, b) => new Date(a) - new Date(b))       // ascending for schedule
        .map(dateKey => ({ dateKey, games: map[dateKey] }));
};

const SchedulePanel = () => {
    const { adminId, activeSeason } = useAdmin();

    const [allGames,        setAllGames]        = useState([]);
    const [grouped,         setGrouped]         = useState([]);
    const [showPast,        setShowPast]        = useState(true);
    const [showBuilder,     setShowBuilder]     = useState(false);
    const [editingGame,     setEditingGame]     = useState(null);   // draft copy
    const [loadingSchedule, setLoadingSchedule] = useState(false);

    // ── Load ─────────────────────────────────────────────────────────────────
    const loadSchedule = useCallback(async () => {
        if (!activeSeason) return;
        setLoadingSchedule(true);
        const { data, error } = await supabase
            .from('games')
            .select(`
                *,
                team1:teams!games_team1_id_fkey(id, name, captain_id, logo_url),
                team2:teams!games_team2_id_fkey(id, name, captain_id, logo_url)
            `)
            .eq('game_type', 'regular')
            .eq('season_id', activeSeason.id)
            .order('game_date', { ascending: true });

        if (!error) setAllGames(data || []);
        setLoadingSchedule(false);
    }, [activeSeason]);

    useEffect(() => { loadSchedule(); }, [loadSchedule]);

    // ── Filter + group whenever source data or showPast changes ──────────────
    useEffect(() => {
        const todayStart = (() => {
            const n = new Date();
            return new Date(n.getFullYear(), n.getMonth(), n.getDate());
        })();

        const filtered = showPast
            ? allGames
            : allGames.filter(g => parseLocalDate(g.game_date) >= todayStart);

        setGrouped(groupByDate(filtered));
    }, [allGames, showPast]);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleDeleteScheduled = async (gameId) => {
        if (!window.confirm('Delete this game from the schedule?')) return;
        try {
            await supabase.functions.invoke('admin-actions', {
                body: { action: 'deleteScheduledGame', adminId, payload: { gameId } },
            });
            await loadSchedule();
        } catch (err) {
            alert(`Error: ${err.message}`);
        }
    };

    const handleSaveEdit = async () => {
        if (!editingGame) return;
        try {
            await supabase.functions.invoke('admin-actions', {
                body: {
                    action: 'updateScheduledGame',
                    adminId,
                    payload: { gameId: editingGame.id, game_date: editingGame.game_date, court: editingGame.court || null },
                },
            });
            setEditingGame(null);
            await loadSchedule();
        } catch (err) {
            alert(`Error updating game: ${err.message}`);
        }
    };

    const handlePrint = async (game) => {
        const [{ data: t1 }, { data: t2 }] = await Promise.all([
            supabase.from('players').select('id, name, jersey_number')
                .eq('team_id', game.team1_id).order('jersey_number', { ascending: true }),
            supabase.from('players').select('id, name, jersey_number')
                .eq('team_id', game.team2_id).order('jersey_number', { ascending: true }),
        ]);
        printScoreSheet(game, t1 || [], t2 || []);
    };

    const generateBracket = async () => {
        if (!window.confirm('Generate the playoff bracket from current standings? This creates the 15-game bracket (Round 1 filled from seeding).')) return;
        const { error } = await supabase.rpc('generate_playoff_bracket', { p_season_id: activeSeason.id });
        alert(error ? `Error: ${error.message}` : 'Playoff bracket generated!');
        await loadSchedule();
    };

    // ── Today check (local-safe) ──────────────────────────────────────────────
    const isToday = (dateKey) => {
        const n = new Date();
        const todayKey = new Date(n.getFullYear(), n.getMonth(), n.getDate())
            .toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
        return dateKey === todayKey;
    };

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Season Schedule</h2>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <input
                        type="checkbox"
                        checked={showPast}
                        onChange={e => setShowPast(e.target.checked)}
                        className="w-4 h-4 accent-orange-600"
                    />
                    <span className="text-gray-700 dark:text-gray-300">Show Past Games</span>
                </label>
            </div>

            {activeSeason?.status === 'playoffs' && (
                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl flex items-center justify-between gap-4">
                    <div>
                        <h3 className="font-bold text-purple-900 dark:text-purple-300">Playoffs</h3>
                        <p className="text-sm text-purple-700 dark:text-purple-400">
                            Generate the 15-game bracket from current standings. One-time action.
                        </p>
                    </div>
                    <button
                        onClick={generateBracket}
                        className="shrink-0 px-4 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-semibold"
                    >
                        Generate Playoff Bracket
                    </button>
                </div>
            )}

            {/* Schedule Builder toggle */}
            {showBuilder
                ? <ScheduleBuilder onBack={() => { setShowBuilder(false); loadSchedule(); }} />
                : (
                    <button
                        onClick={() => setShowBuilder(true)}
                        className="bg-orange-600 hover:bg-orange-700 text-white py-2 px-4 rounded-lg font-semibold transition"
                    >
                        Open Schedule Builder
                    </button>
                )
            }

            {/* Game list */}
            {!showBuilder && (
                <div>
                    <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                        {showPast ? 'All Games' : 'Upcoming Games'}
                    </h3>

                    {loadingSchedule ? (
                        <p className="text-gray-500 dark:text-gray-400 text-center py-8">Loading schedule…</p>
                    ) : grouped.length === 0 ? (
                        <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-12 text-center">
                            <p className="text-gray-500 dark:text-gray-400">
                                {showPast ? 'No games found.' : 'No upcoming games scheduled yet.'}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-10">
                            {grouped.map(({ dateKey, games }) => (
                                <div key={dateKey} className="space-y-4">
                                    {/* Day Header */}
                                    <div className="flex items-center gap-3 sticky top-4 z-10 bg-gray-100 dark:bg-gray-900 py-2">
                                        <div className="font-bold text-xl text-gray-900 dark:text-white">{dateKey}</div>
                                        {isToday(dateKey) && (
                                            <span className="px-3 py-1 text-xs font-semibold bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 rounded-full">
                                                TODAY
                                            </span>
                                        )}
                                    </div>

                                    <button
                                        onClick={() => generateSchedulePoster(dateKey, games, activeSeason)}
                                        className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-5 py-2 rounded-xl text-sm font-semibold transition"
                                    >
                                        📸 Generate Weekly Poster
                                    </button>

                                    <div className="space-y-3 pl-4">
                                        {games.map(game => (
                                            <div key={game.id}
                                                className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-2xl p-5
                                                           flex flex-col md:flex-row md:items-center gap-4 hover:shadow-md transition"
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-3">
                                                        <div className="font-bold text-lg text-orange-600 dark:text-orange-400 truncate">
                                                            {game.team1?.name} vs {game.team2?.name}
                                                        </div>
                                                        {game.court && (
                                                            <span className="text-xs px-2.5 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                                                                {game.court}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                                        {parseLocalDate(game.game_date).toLocaleTimeString('en-US', {
                                                            hour: 'numeric', minute: '2-digit',
                                                        })}
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-2 shrink-0">
                                                    <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                                                        game.status === 'completed'   ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                                                        : game.status === 'in_progress' ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                                                        :                                  'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300'
                                                    }`}>
                                                        {game.status}
                                                    </span>
                                                    <button onClick={() => setEditingGame({ ...game })}
                                                        className="px-4 py-2 text-sm dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700">
                                                        Edit
                                                    </button>
                                                    <button onClick={() => handlePrint(game)}
                                                        className="px-4 py-2 text-sm dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700">
                                                        🖨 Print
                                                    </button>
                                                    <button onClick={() => handleDeleteScheduled(game.id)}
                                                        className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-xl transition">
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Edit Modal */}
            {editingGame && (
                <EditScheduledGameModal
                    game={editingGame}
                    onChange={setEditingGame}
                    onSave={handleSaveEdit}
                    onClose={() => setEditingGame(null)}
                />
            )}
        </div>
    );
};

export default SchedulePanel;